import axios from "axios";
import { PreisPreviewLoader } from "@k/global/preisAnzeiger";
import {LieferterminController} from "@s/js/liefertermine/lieferterminController";
import {sendPostRequest} from "@k/global/axiosFormWrapper";

export type ProduktionsartKey = "standard" | "express" | "superexpress";

export type ProduktionsTermin = {
    produktionsart: ProduktionsartKey;
    versandtermin: string; // "2026-03-12 00:00:00"
    bestelltermin: string;
    freigabetermin: string;
    maxBestellmenge: string;
    naeherei: string;
};

export type PuLPreviewResponse = {
    termine: {
        standardtermine: ProduktionsTermin;
        expresstermine: ProduktionsTermin;
        superexpresstermine: ProduktionsTermin;
    };
    preise: {
        preis: number | string;
        originalPreis: number | string;
        symbol: string;
        isEUStaat: boolean;
        isStandardPriceList: boolean;
        inklusiveMwSt: boolean;
        waehrungsKuerzel: string;
    };
};

export type PuLShippingEntry = {
    earliest: string; // "2026-03-13T00:00:00"
    latest: string;
    valuesAreDates: boolean;
    hatLaufzeitBerechnung: boolean;
    spediteur: string;
};
export type PuLShippingResponse = Record<string, PuLShippingEntry>;

export type PuLState = {
    amount: number | null;
    country: string;
    selectedProduction: ProduktionsartKey;
    preview: PuLPreviewResponse | null;
    shipping: PuLShippingResponse | null;
    loadingPreview: boolean;
    loadingShipping: boolean;
    error: string | null;
};

function parsePhpDateTimeToDate(value: string): Date | null {
    if (!value) return null;
    const isoLike = value.includes("T") ? value : value.replace(" ", "T");
    const d = new Date(isoLike);
    return isNaN(d.getTime()) ? null : d;
}

function getVersandterminByProduction(preview: PuLPreviewResponse, production: ProduktionsartKey): string | null {
    const t = preview?.termine;
    if (!t) return null;

    const terminObj =
        production === "express" ? t.expresstermine :
            production === "superexpress" ? t.superexpresstermine :
                t.standardtermine;

    const d = parsePhpDateTimeToDate(terminObj?.versandtermin);
    if (!d) return null;

    // entspricht ActionViewComponent
    return d.toDateString();
}

export default  {
    namespaced: true,
    state: (): PuLState => ({
        amount: null,
        country: "",
        selectedProduction: "standard",

        preview: null,
        shipping: null,

        loadingPreview: false,
        loadingShipping: false,

        error: null,
    }),
    mutations: {
        SET_PUL_AMOUNT(state: PuLState, amount: number | null) {
            state.amount = amount;
        },
        SET_PUL_COUNTRY(state: PuLState, country: string) {
            state.country = country;
        },
        SET_PUL_PARAMS(state: PuLState, payload: { amount: number; country: string }) {
            state.amount = payload.amount;
            state.country = payload.country;
        },

        SET_SELECTED_PRODUCTION(state: PuLState, production: ProduktionsartKey) {
            state.selectedProduction = production;
        },

        SET_PREVIEW(state: PuLState, preview: PuLPreviewResponse | null) {
            state.preview = preview;
        },
        SET_SHIPPING(state: PuLState, shipping: PuLShippingResponse | null) {
            state.shipping = shipping;
        },

        SET_LOADING_PREVIEW(state: PuLState, value: boolean) {
            state.loadingPreview = value;
        },
        SET_LOADING_SHIPPING(state: PuLState, value: boolean) {
            state.loadingShipping = value;
        },

        SET_ERROR(state: PuLState, msg: string | null) {
            state.error = msg;
        },

        RESET(state: PuLState) {
            state.amount = null;
            state.country = "";
            state.selectedProduction = "standard";
            state.preview = null;
            state.shipping = null;
            state.loadingPreview = false;
            state.loadingShipping = false;
            state.error = null;
        },
    },
    actions: {
        async LOAD_PREVIEW_AND_SHIPPING(context, payload: { amount: number; country: string }) {
            context.commit("SET_PUL_PARAMS", payload);
            context.commit("SET_SELECTED_PRODUCTION", "standard");
            context.commit("SET_ERROR", null);

            const currentProduct = context.rootGetters["ModelStore/getCurrentOwayoProduct"];
            if (!currentProduct) {
                context.commit("SET_ERROR", "noProduct");
                return;
            }

            context.commit("SET_LOADING_PREVIEW", true);
            context.commit("SET_PREVIEW", null);
            context.commit("SET_SHIPPING", null);

            try {
                const loader = new PreisPreviewLoader(
                    payload.country,
                    context.rootGetters["ModelStore/getGlobalRabattCode"],
                    context.rootGetters.getEntwurfData?.logoObjects,
                    context.rootGetters.getEntwurfData?.textObjects
                );
                loader.setProductInfoFromOwayoProduct(
                    currentProduct,
                    context.rootGetters.getHerstellerlogosEntfernt
                );

                const preview = await loader.getPriceAndDeliveryTimeFromServerAsPromise(payload.amount);
                context.commit("SET_PREVIEW", preview as PuLPreviewResponse);
            } catch (e) {
                context.commit("SET_ERROR", "previewLoadFailed");
                throw e;
            } finally {
                context.commit("SET_LOADING_PREVIEW", false);
            }

            await context.dispatch("LOAD_SHIPPING");
        },

        async LOAD_SHIPPING(context) {
            const preview: PuLPreviewResponse | null = context.state.preview;
            if (!preview) return;

            const amount = context.state.amount;
            const country = context.state.country;
            if (!amount || amount < 1 || !country) return;

            const versandtermin = getVersandterminByProduction(preview, context.state.selectedProduction);
            if (!versandtermin) {
                context.commit("SET_ERROR", "noVersandtermin");
                return;
            }

            const currentProduct = context.rootGetters["ModelStore/getCurrentOwayoProduct"];
            if (!currentProduct) return;

            context.commit("SET_LOADING_SHIPPING", true);
            context.commit("SET_SHIPPING", null);

            try {
                sendPostRequest("/shoppingcart/php/versandkosten/getVersandOptionenInklLiefertermine.php",
                    {
                        versandtermin: versandtermin,
                        land: country,
                        produkt: currentProduct.produkt,
                        dvschnitt: currentProduct.cut,
                        amount: amount,
                        features: currentProduct.selectedFeatures ?? [],
                    })
                    .then(response=>context.commit("SET_SHIPPING", response.data as PuLShippingResponse));
            } catch (e) {
                context.commit("SET_ERROR", "shippingLoadFailed");
                throw e;
            } finally {
                context.commit("SET_LOADING_SHIPPING", false);
            }
        },
    },
    getters: {
        amount: (state: PuLState) => state.amount,
        country: (state: PuLState) => state.country,
        selectedProduction: (state: PuLState) => state.selectedProduction,
        preview: (state: PuLState) => state.preview,
        shipping: (state: PuLState) => state.shipping,
        loadingPreview: (state: PuLState) => state.loadingPreview,
        loadingShipping: (state: PuLState) => state.loadingShipping,
        error: (state: PuLState) => state.error,
        isValid: (state: PuLState) => !!state.amount && state.amount > 0 && !!state.country,
    },
};